import type { Editor, Extensions } from "@tiptap/core";
import { FileHandler } from "@tiptap/extension-file-handler";
import {
  Image as TiptapImage,
  type ImageOptions,
} from "@tiptap/extension-image";
import { ImageUploadNode } from "src/tiptap/components/tiptap-node/image-upload-node/image-upload-node-extension";

type ImageResizeOptions = Exclude<ImageOptions["resize"], false>;

export const IMAGE_UPLOAD_MAX_SIZE = 5 * 1024 * 1024;
export const IMAGE_UPLOAD_LIMIT = 3;
export const IMAGE_UPLOAD_ACCEPT = "image/*";

const getImageSizeStyle = (value: unknown) => {
  const size =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : NaN;

  return Number.isFinite(size) && size > 0 ? `${size}px` : null;
};

const escapeHtmlAttribute = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");

const getImageSizeAttribute = (value: unknown) => {
  const style = getImageSizeStyle(value);

  return style ? style.slice(0, -2) : null;
};

// Standard Markdown images cannot retain editor dimensions, so sized images use inline HTML.
const renderImageMarkdown = (attrs: Record<string, unknown>) => {
  const src = typeof attrs.src === "string" ? attrs.src : "";
  const alt = typeof attrs.alt === "string" ? attrs.alt : "";
  const title = typeof attrs.title === "string" ? attrs.title : "";
  const width = getImageSizeAttribute(attrs.width);
  const height = getImageSizeAttribute(attrs.height);

  if (!width && !height) {
    return title ? `![${alt}](${src} "${title}")` : `![${alt}](${src})`;
  }

  return [
    `<img src="${escapeHtmlAttribute(src)}"`,
    ` alt="${escapeHtmlAttribute(alt)}"`,
    title ? ` title="${escapeHtmlAttribute(title)}"` : "",
    width ? ` width="${width}"` : "",
    height ? ` height="${height}"` : "",
    " />",
  ].join("");
};

const setImageSizeStyle = (
  element: HTMLImageElement,
  property: "width" | "height",
  value: unknown
) => {
  const styleValue = getImageSizeStyle(value);

  if (styleValue) {
    element.style[property] = styleValue;
  } else {
    element.style.removeProperty(property);
  }
};

const syncImageElementAttributes = (
  element: HTMLImageElement,
  attrs: Record<string, unknown>
) => {
  setImageSizeStyle(element, "width", attrs.width);
  setImageSizeStyle(element, "height", attrs.height);

  for (const name of ["src", "alt", "title"] as const) {
    const value = attrs[name];
    if (typeof value === "string") {
      element.setAttribute(name, value);
    } else {
      element.removeAttribute(name);
    }
  }
};

export const formatImageUploadSize = (bytes: number): string => {
  const megabytes = bytes / 1024 / 1024;
  return `${Number.isInteger(megabytes) ? megabytes : megabytes.toFixed(1)}MB`;
};

export const validateImageFile = (
  file: File,
  options: { maxSize: number }
) => {
  if (!file.type?.startsWith("image/")) {
    throw new Error("Only image files can be uploaded.");
  }

  if (options.maxSize > 0 && file.size > options.maxSize) {
    throw new Error(
      `Images must be ${formatImageUploadSize(options.maxSize)} or smaller.`
    );
  }
};

export const validateImageFiles = (
  files: File[],
  options: { maxSize: number; limit: number }
) => {
  if (!files.length) {
    throw new Error("No files to upload.");
  }

  if (options.limit > 0 && files.length > options.limit) {
    throw new Error(
      `Upload up to ${options.limit} image${options.limit === 1 ? "" : "s"} at a time.`
    );
  }

  files.forEach((file) => validateImageFile(file, options));
};

export const IMAGE_RESIZE_OPTIONS = {
  enabled: true,
  directions: ["top-left", "top-right", "bottom-left", "bottom-right"],
  minWidth: 48,
  minHeight: 48,
  alwaysPreserveAspectRatio: true,
} satisfies ImageResizeOptions;

export type UploadedImageAttrs = {
  src: string;
  width?: number | string | null;
  height?: number | string | null;
};

export type ImageUploadResult = {
  url: string;
};

export type UploadImage = (file: File) => Promise<ImageUploadResult>;

type ImageUploadAdapterOptions = {
  uploadImage: UploadImage;
  maxSize?: number;
};

type UploadAndInsertImagesOptions = ImageUploadAdapterOptions & {
  editor: Editor;
  files: File[];
  limit?: number;
  position?: number;
  onError?: (error: Error) => void;
  onSuccess?: (result: UploadedImageAttrs) => void;
};

type ImageUploadKitOptions = Omit<
  UploadAndInsertImagesOptions,
  "editor" | "files" | "position"
> & {
  resize?: boolean;
};

const ImageBase = TiptapImage.extend({
  renderMarkdown: (node) => renderImageMarkdown(node.attrs ?? {}),

  addNodeView() {
    const parentNodeView = this.parent?.();

    if (!parentNodeView) {
      return null;
    }

    return (props) => {
      const nodeView = parentNodeView(props);
      const dom = nodeView.dom as HTMLElement;
      const imageElement =
        dom instanceof HTMLImageElement ? dom : dom.querySelector("img");

      if (!imageElement) {
        return nodeView;
      }

      syncImageElementAttributes(imageElement, props.node.attrs);

      const originalUpdate = nodeView.update?.bind(nodeView);
      nodeView.update = (node, decorations, innerDecorations) => {
        const didUpdate = originalUpdate
          ? originalUpdate(node, decorations, innerDecorations)
          : node.type === props.node.type;

        if (didUpdate) {
          syncImageElementAttributes(imageElement, node.attrs);
        }

        return didUpdate;
      };

      return nodeView;
    };
  },
});

export const createImageExtension = ({
  resize = true,
}: {
  resize?: boolean;
} = {}) =>
  ImageBase.configure({
    resize: resize ? IMAGE_RESIZE_OPTIONS : false,
  });

export const createImageUploadAdapter =
  ({
    uploadImage,
    maxSize = IMAGE_UPLOAD_MAX_SIZE,
  }: ImageUploadAdapterOptions) =>
  async (file: File): Promise<UploadedImageAttrs> => {
    validateImageFile(file, { maxSize });

    const { url } = await uploadImage(file);

    if (!url) {
      throw new Error("Uploaded image did not return a URL.");
    }

    return { src: url };
  };

export const uploadAndInsertImages = async ({
  editor,
  files,
  uploadImage,
  maxSize = IMAGE_UPLOAD_MAX_SIZE,
  limit = IMAGE_UPLOAD_LIMIT,
  position,
  onError,
  onSuccess,
}: UploadAndInsertImagesOptions) => {
  if (!editor.isEditable) {
    return false;
  }

  try {
    validateImageFiles(files, { maxSize, limit });

    const upload = createImageUploadAdapter({ uploadImage, maxSize });
    const imageAttrs = await Promise.all(files.map((file) => upload(file)));
    const imageNodes = imageAttrs.map((attrs) => ({
      type: "image",
      attrs,
    }));

    const chain = editor.chain().focus();
    const inserted =
      typeof position === "number"
        ? chain.insertContentAt(position, imageNodes).run()
        : chain.insertContent(imageNodes).run();

    if (inserted) {
      imageAttrs.forEach((attrs) => onSuccess?.(attrs));
    }

    return inserted;
  } catch (error) {
    onError?.(error instanceof Error ? error : new Error("Image upload failed"));
    return false;
  }
};

const createImageFileHandler = ({
  uploadImage,
  maxSize = IMAGE_UPLOAD_MAX_SIZE,
  limit = IMAGE_UPLOAD_LIMIT,
  onError,
  onSuccess,
}: ImageUploadKitOptions) =>
  FileHandler.configure({
    onPaste: (editor, files) => {
      void uploadAndInsertImages({
        editor,
        files,
        uploadImage,
        maxSize,
        limit,
        onError,
        onSuccess,
      });
    },
    onDrop: (editor, files, position) => {
      void uploadAndInsertImages({
        editor,
        files,
        uploadImage,
        maxSize,
        limit,
        position,
        onError,
        onSuccess,
      });
    },
  });

export const createImageUploadKit = ({
  resize = true,
  uploadImage,
  maxSize = IMAGE_UPLOAD_MAX_SIZE,
  limit = IMAGE_UPLOAD_LIMIT,
  onError,
  onSuccess,
}: ImageUploadKitOptions): { extensions: Extensions } => ({
  extensions: [
    createImageExtension({ resize }),
    ImageUploadNode.configure({
      accept: IMAGE_UPLOAD_ACCEPT,
      maxSize,
      limit,
      uploadImage,
      onError,
      onSuccess,
    }),
    createImageFileHandler({
      uploadImage,
      maxSize,
      limit,
      onError,
      onSuccess,
    }),
  ],
});
